var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as React from 'react';
import { isString } from '../_internal/data';
import { searchInString } from '../_internal/strings';
import { stringifyColor } from '../color';
import { ActionType, } from './Select.interface';
var DEFAULT_MULTI_VALUE = [];
var DEFAULT_ON_CHANGE = function () { };
var reducer = function (state, action) {
    switch (action.type) {
        case ActionType.UpdateQuery: {
            return __assign(__assign({}, state), { query: action.value, isOpened: true });
        }
        case ActionType.Open: {
            if (state.isOpened) {
                return state;
            }
            return __assign(__assign({}, state), { query: '', isOpened: true, focusedOption: undefined });
        }
        case ActionType.Close: {
            if (!state.isOpened) {
                return state;
            }
            return __assign(__assign({}, state), { isOpened: false });
        }
        case ActionType.SetShowResetIcon: {
            if (state.showResetIcon === action.value) {
                return state;
            }
            return __assign(__assign({}, state), { showResetIcon: action.value });
        }
        case ActionType.SetFocusedOption: {
            return __assign(__assign({}, state), { focusedOption: action.value });
        }
        default: {
            return state;
        }
    }
};
var INITIAL_STATE = {
    isOpened: false,
    query: '',
    showResetIcon: false,
    focusedOption: null,
};
export var useSelect = function (_a) {
    var rawValue = _a.value, multi = _a.multi, options = _a.options, _b = _a.onChange, onChange = _b === void 0 ? DEFAULT_ON_CHANGE : _b;
    var _c = React.useReducer(reducer, INITIAL_STATE), state = _c[0], dispatch = _c[1];
    var value = React.useMemo(function () {
        if (multi) {
            return rawValue !== null && rawValue !== void 0 ? rawValue : DEFAULT_MULTI_VALUE;
        }
        return rawValue;
    }, [multi, rawValue]);
    var selectedOptions = React.useMemo(function () {
        if (multi && Array.isArray(value)) {
            var multiValue_1 = value;
            return options.filter(function (option) {
                return multiValue_1 === null || multiValue_1 === void 0 ? void 0 : multiValue_1.some(function (val) { return val === option.value; });
            });
        }
        return options.filter(function (option) { return option.value === value; });
    }, [multi, options, value]);
    var visibleOptions = React.useMemo(function () {
        return options
            .filter(function (option) {
            var _a;
            var keywords = __spreadArray(__spreadArray([
                "".concat(option.value)
            ], (isString(option.label) ? [option.label] : []), true), ((_a = option.keywords) !== null && _a !== void 0 ? _a : []), true);
            return keywords.some(function (keyword) {
                return searchInString(keyword, state.query);
            });
        })
            .map(function (option) { return (__assign(__assign({}, option), { selected: selectedOptions.some(function (option2) { return option2.value === option.value; }) })); });
    }, [selectedOptions, options, state.query]);
    var label = React.useMemo(function () {
        return selectedOptions.length > 0
            ? selectedOptions.map(function (option) { return option.label; }).join(', ')
            : null;
    }, [selectedOptions]);
    var color = React.useMemo(function () {
        var _a;
        var result = (_a = selectedOptions.find(function (el) { return el.color; })) === null || _a === void 0 ? void 0 : _a.color;
        return result && stringifyColor(result);
    }, [selectedOptions]);
    var areAllOptionsSelected = React.useMemo(function () {
        if (multi && Array.isArray(value)) {
            var multiValue = value;
            return options.length === multiValue.length;
        }
        return false;
    }, [multi, options.length, value]);
    var handleSearch = React.useCallback(function (e) { return dispatch({ type: ActionType.UpdateQuery, value: e.target.value }); }, []);
    var handleOpen = React.useCallback(function () {
        dispatch({ type: ActionType.Open });
    }, []);
    var handleClose = React.useCallback(function () { return dispatch({ type: ActionType.Close }); }, []);
    var handleSelectOne = React.useCallback(function (option) {
        onChange(option.value);
        dispatch({ type: ActionType.SetFocusedOption, value: option.value });
    }, [onChange]);
    var handleSelectMulti = React.useCallback(function (option) {
        var multiValue = (value !== null && value !== void 0 ? value : []);
        var newValue = multiValue.includes(option.value)
            ? multiValue.filter(function (el) { return el !== option.value; })
            : __spreadArray(__spreadArray([], multiValue, true), [option.value], false);
        onChange(newValue);
    }, [onChange, value]);
    var handleSelect = React.useCallback(function (option) {
        dispatch({ type: ActionType.SetFocusedOption, value: undefined });
        if (multi) {
            handleSelectMulti(option);
        }
        else {
            handleSelectOne(option);
            handleClose();
        }
    }, [handleSelectMulti, handleSelectOne, handleClose, multi]);
    var handleSelectAll = React.useCallback(function (selectAll) {
        if (selectAll) {
            onChange(options.map(function (option) { return option.value; }));
        }
        else {
            onChange([]);
        }
    }, [onChange, options]);
    var handleReset = React.useCallback(function (e) {
        e.stopPropagation();
        dispatch({ type: ActionType.SetShowResetIcon, value: false });
        onChange(multi ? [] : null);
    }, [multi, onChange]);
    React.useEffect(function () {
        var handleKeyDown = function (event) {
            var _a, _b;
            var key = event.key, shiftKey = event.shiftKey;
            if (state.isOpened) {
                if (key === 'ArrowDown' || (key === 'Tab' && !shiftKey)) {
                    event.preventDefault();
                    var focusedIndex = visibleOptions.findIndex(function (option) { return option.value === state.focusedOption; });
                    dispatch({
                        type: ActionType.SetFocusedOption,
                        value: (_a = options[focusedIndex < visibleOptions.length - 1 ? focusedIndex + 1 : 0]) === null || _a === void 0 ? void 0 : _a.value,
                    });
                }
                if (key === 'ArrowUp' || (key === 'Tab' && shiftKey)) {
                    event.preventDefault();
                    var focusedIndex = visibleOptions.findIndex(function (option) { return option.value === state.focusedOption; });
                    dispatch({
                        type: ActionType.SetFocusedOption,
                        value: (_b = options[focusedIndex > 0 ? focusedIndex - 1 : visibleOptions.length - 1]) === null || _b === void 0 ? void 0 : _b.value,
                    });
                }
                if (key === 'Enter') {
                    var focusedOption = visibleOptions.find(function (option) { return option.value === state.focusedOption; });
                    if (focusedOption) {
                        handleSelect(focusedOption);
                    }
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return function () {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        handleSelect,
        options,
        state.focusedOption,
        state.isOpened,
        visibleOptions,
    ]);
    var actions = {
        onReset: handleReset,
        onOpen: handleOpen,
        onClose: handleClose,
        onSelectAll: handleSelectAll,
        onSelect: handleSelect,
        onSearch: handleSearch,
    };
    var fullState = __assign(__assign({}, state), { label: label, visibleOptions: visibleOptions, areAllOptionsSelected: areAllOptionsSelected, color: color, selectedOptions: selectedOptions });
    return [fullState, actions];
};
//# sourceMappingURL=Select.hooks.js.map